import React, { useState, useEffect } from 'react';
import { faviconService } from '../../services/faviconService';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { getAuth } from 'firebase/auth';

interface FaviconData {
  url: string;
  type: string;
  updatedAt: string;
}

export const FaviconManager: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [favicon, setFavicon] = useState<FaviconData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFavicon();
  }, []);

  const loadFavicon = async () => {
    try {
      const data = await faviconService.getFavicon();
      setFavicon(data);
    } catch (err) {
      setError('Failed to load favicon');
      console.error('Error loading favicon:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Ensure user is authenticated
    const auth = getAuth();
    if (!auth.currentUser) {
      setError('You must be logged in to upload a favicon');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await faviconService.updateFavicon(file);
      await loadFavicon();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update favicon');
      console.error('Error updating favicon:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-primary-900">Favicon</h3>
      
      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Current Favicon Display */}
      <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-md">
        <div className="flex-shrink-0">
          {favicon ? (
            <img
              src={favicon.url}
              alt="Current favicon"
              className="w-8 h-8 object-contain"
            />
          ) : (
            <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-gray-400 text-xs">No favicon</span>
            </div>
          )}
        </div>
        <div className="flex-grow">
          <p className="text-sm text-gray-600">
            {favicon ? (
              <>
                Last updated: {new Date(favicon.updatedAt).toLocaleDateString()}
                <br />
                <span className="text-xs">Type: {favicon.type}</span>
              </>
            ) : (
              'No favicon uploaded'
            )}
          </p>
        </div>
      </div>

      {/* Upload New Favicon */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-primary-700 mb-2">
          Upload New Favicon
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex-grow">
            <div className="relative">
              <input
                type="file"
                className="hidden"
                accept=".ico,.png,.svg"
                onChange={handleFileSelect}
                disabled={isSaving || !user}
              />
              <div className={`px-4 py-2 border border-primary-300 rounded-md text-center ${user ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed bg-gray-100'}`}>
                {isSaving ? (
                  <span className="text-gray-500">Uploading...</span>
                ) : !user ? (
                  <span className="text-gray-500">Please log in to upload</span>
                ) : (
                  <span className="text-primary-700">Choose File</span>
                )}
              </div>
            </div>
          </label>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Accepted formats: .ico, .png, .svg (recommended size: 32x32 pixels)
        </p>
      </div>
    </div>
  );
}; 